import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import { WorkQueue, WorkQueueItem, sendReminder } from "api/Dashboard";
import { Panel, EmptyState, currency } from "./shared";

/**
 * What to do next.
 *
 * NO PERIOD FILTER, EVER. An invoice 132 days late is not August's
 * problem or September's; it is late. The period control at the top of
 * the page does not reach this panel by design (spec 3.2).
 *
 * FILTERED BY KIND, NOT BY DATE, and each kind carries its own total
 * rather than one combined "at risk" figure (spec 7.5): overdue money is
 * earned and sitting in someone's AP, a lapsing quote is speculative and
 * may never exist. Summing them would imply they are the same kind of
 * number. Kind also happens to be how the work divides between people.
 *
 * Rows are computed from current state, so they disappear when the
 * underlying thing resolves. There is nothing to tick off and nothing
 * to clear.
 */

type KindKey = "all" | "money" | "billing" | "client" | "system";

const WorkQueuePanel: React.FC<{ queue: WorkQueue }> = ({ queue }) => {
  const [kind, setKind] = useState<KindKey>("all");

  // Reminder state. `confirming` holds the row awaiting a yes - sending
  // a client an email is not something a single mis-click should do.
  const [confirming, setConfirming] = useState<WorkQueueItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const runReminder = async (item: WorkQueueItem) => {
    if (!item.entityId) return;
    setConfirming(null);
    setBusyId(item.id);
    try {
      const res = await sendReminder("invoice", item.entityId);
      if (res.sent) {
        setResult("Reminder sent to " + (res.recipient || "the client") + ".");
      } else if (res.throttledUntil) {
        // The throttle is reported, never silent - an operator who
        // thinks they sent something and did not is worse off than one
        // who is told it already went.
        setResult(
          "A reminder already went out for this invoice in the last 24 hours.",
        );
      }
    } catch (e: any) {
      setResult(
        e?.response?.data?.message || "That reminder could not be sent.",
      );
    } finally {
      setBusyId(null);
    }
  };
  const navigate = useNavigate();

  const groups =
    kind === "all" ? queue.groups : queue.groups.filter((g) => g.kind === kind);
  const items = groups.flatMap((g) => g.items);

  const filters: Array<{ key: KindKey; label: string; count: number }> = [
    { key: "all", label: "All", count: queue.totalCount },
    ...queue.groups.map((g) => ({
      key: g.kind as KindKey,
      label: g.label,
      count: g.count,
    })),
  ];

  const severityColor = (severity: string) =>
    severity === "critical"
      ? themevals.colors.danger
      : severity === "warning"
        ? themevals.colors.warning
        : themevals.colors.info;

  return (
    <Panel
      title="Needs attention"
      subtitle="Not filtered by period. Overdue is overdue."
      right={
        <Flex sx={{ gap: "4px", flexWrap: "wrap" }}>
          {filters.map((f) => {
            const active = f.key === kind;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setKind(f.key)}
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: active ? 600 : 500,
                  padding: "5px 10px",
                  borderRadius: "6px",
                  border: "1px solid " + themevals.colors.inputOutline,
                  backgroundColor: active ? themevals.colors.base_800 : "#FFFFFF",
                  color: active ? "#FFFFFF" : themevals.colors.dkgrey,
                  cursor: "pointer",
                }}
              >
                {f.label + " " + f.count}
              </button>
            );
          })}
        </Flex>
      }
      sx={{ marginBottom: "24px" }}
    >
      {confirming && (
        <Box
          sx={{
            padding: "12px 16px",
            marginBottom: "12px",
            borderRadius: "6px",
            backgroundColor: "#FFF8E1",
            border: "1px solid #E7CC7A",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            color: themevals.colors.base_800,
          }}
        >
          {/* The record is named, because "send a reminder?" with no
              subject is how the wrong client gets emailed. */}
          <Box sx={{ marginBottom: "10px" }}>
            Email a reminder about {confirming.title}? It goes to the client
            contact on that invoice.
          </Box>
          <Flex sx={{ gap: "8px" }}>
            <button
              type="button"
              onClick={() => runReminder(confirming)}
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                padding: "6px 14px",
                borderRadius: "6px",
                border: "1px solid " + themevals.colors.base_800,
                backgroundColor: themevals.colors.base_800,
                color: "#FFFFFF",
                cursor: "pointer",
              }}
            >
              Send reminder
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                padding: "6px 14px",
                borderRadius: "6px",
                border: "1px solid " + themevals.colors.inputOutline,
                backgroundColor: "#FFFFFF",
                color: themevals.colors.dkgrey,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </Flex>
        </Box>
      )}

      {result && (
        <Flex
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 14px",
            marginBottom: "12px",
            borderRadius: "6px",
            backgroundColor: "#F7F9FC",
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: themevals.colors.base_800,
          }}
        >
          <Box>{result}</Box>
          <button
            type="button"
            onClick={() => setResult(null)}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              border: "none",
              background: "none",
              color: themevals.colors.dkgrey,
              cursor: "pointer",
            }}
          >
            Dismiss
          </button>
        </Flex>
      )}

      {/*
        Per-kind totals. Deliberately no combined figure: money owed to
        us, work not yet billed, and speculative client value are three
        different kinds of number.
      */}
      <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "12px" }}>
        {groups.map((g) => (
          <Box
            key={g.kind}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              color: themevals.colors.dkgrey,
            }}
          >
            <Box sx={{ fontWeight: 600, color: "base_800" }}>
              {g.amount === null
                ? g.count + (g.count === 1 ? " item" : " items")
                : currency(g.amount)}
            </Box>
            {g.label + (g.amount === null ? " (no money attached)" : "")}
          </Box>
        ))}
      </Flex>

      {items.length === 0 ? (
        <EmptyState
          headline="Nothing needs attention"
          detail="Rows appear here on their own when something falls behind, and disappear when it is dealt with. An empty queue is the goal state, not a missing widget."
        />
      ) : (
        <Box>
          {items.map((item) => (
            <Flex
              key={item.id}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "10px 0",
                borderTop: "1px solid",
                borderColor: themevals.colors.inputOutline,
              }}
            >
              <Flex sx={{ gap: "10px", alignItems: "flex-start", minWidth: 0 }}>
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "3px",
                    backgroundColor: severityColor(item.severity),
                    marginTop: "6px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "base_800",
                    }}
                  >
                    {item.title}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      color: themevals.colors.dkgrey,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.reason}
                  </Box>
                </Box>
              </Flex>
              <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                {item.amount !== null && (
                  <Box
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "base_800",
                    }}
                  >
                    {currency(item.amount)}
                  </Box>
                )}
                {/*
                  Every row goes somewhere now. A row that names a next
                  step and then cannot take anyone there is worse than no
                  row - it spends trust to say nothing. The aggregate
                  rows land on a filtered list; the two integrity rows
                  land on a drill-down, because no list filters on
                  "disagrees with its line items".

                  An overdue INVOICE row is different: "Follow up" now
                  does the following up. It sends the reminder in place
                  rather than navigating away, because the operator's
                  next act was always going to be chasing this invoice -
                  and a queue you can work without leaving it is the
                  point of the queue (RC5, work in place).
                */}
                {/*
                  View first, on every row that has a record behind it.
                  "Follow up" alone meant chasing an invoice you could
                  not look at from here - nobody sends a reminder about
                  money without checking what it is for.
                */}
                {item.entityType === "invoice" && item.entityId && (
                  <button
                    type="button"
                    onClick={() => navigate(item.actionPath)}
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 500,
                      padding: "5px 12px",
                      borderRadius: "6px",
                      border: "1px solid " + themevals.colors.inputOutline,
                      backgroundColor: "#FFFFFF",
                      color: themevals.colors.base_800,
                      cursor: "pointer",
                    }}
                  >
                    View
                  </button>
                )}
                {item.entityType === "invoice" && item.entityId ? (
                  <button
                    type="button"
                    disabled={busyId === item.id}
                    onClick={() => setConfirming(item)}
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 500,
                      padding: "5px 12px",
                      borderRadius: "6px",
                      border: "1px solid " + themevals.colors.inputOutline,
                      backgroundColor: "#FFFFFF",
                      color: themevals.colors.base_800,
                      cursor: busyId === item.id ? "default" : "pointer",
                    }}
                  >
                    {busyId === item.id ? "Sending..." : item.actionLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(item.actionPath)}
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 500,
                      padding: "5px 12px",
                      borderRadius: "6px",
                      border: "1px solid " + themevals.colors.inputOutline,
                      backgroundColor: "#FFFFFF",
                      color: themevals.colors.base_800,
                      cursor: "pointer",
                    }}
                  >
                    {item.actionLabel}
                  </button>
                )}
              </Flex>
            </Flex>
          ))}
        </Box>
      )}
    </Panel>
  );
};

export default WorkQueuePanel;
